// p5Intellisense.js - Monaco language features for p5.js sketches
// - Uses getP5Signatures/getP5FunctionNames from utils/p5Docs (version-aware docs)
// - Registers signature help, completions, hover, and p5 global typings for JavaScript
// If you add new p5 versions or docs sources, wire them through p5Docs instead of this file.
import { getP5Signatures, getP5FunctionNames } from '../../utils/p5Docs'

let hasRegisteredP5Intellisense = false

export function registerP5Intellisense(monaco, p5Version = '2.1.1') {
  if (hasRegisteredP5Intellisense) {
    return
  }

  hasRegisteredP5Intellisense = true

  const p5Signatures = getP5Signatures(p5Version)
  const p5FunctionNames = getP5FunctionNames(p5Version)

  // Signature Help (parameter hints)
  monaco.languages.registerSignatureHelpProvider('javascript', {
    signatureHelpTriggerCharacters: ['(', ','],
    signatureHelpRetriggerCharacters: [','],
    provideSignatureHelp: (model, position) => {
      const textUntilPosition = model.getValueInRange({
        startLineNumber: position.lineNumber,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column
      })

      const functionMatch = textUntilPosition.match(/\b(\w+)\s*\([^)]*$/)
      if (!functionMatch) return null

      const functionName = functionMatch[1]
      const signature = p5Signatures[functionName]

      if (!signature) return null

      const paramsText = textUntilPosition.split('(').pop()
      const commaCount = (paramsText.match(/,/g) || []).length

      return {
        value: {
          signatures: [{
            label: signature.label,
            documentation: signature.documentation,
            parameters: signature.parameters
          }],
          activeSignature: 0,
          activeParameter: commaCount
        },
        dispose: () => {}
      }
    }
  })

  // Completion items with rich docs
  monaco.languages.registerCompletionItemProvider('javascript', {
    triggerCharacters: ['.'],
    provideCompletionItems: (model, position) => {
      const word = model.getWordUntilPosition(position)
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn
      }

      const suggestions = p5FunctionNames.map((funcName) => {
        const sig = p5Signatures[funcName]
        return {
          label: funcName,
          kind: monaco.languages.CompletionItemKind.Function,
          documentation: {
            value: `**${sig.label}**\n\n${sig.documentation}\n\n**Parameters:**\n${sig.parameters
              .map((p) => `- \`${p.label}\`: ${p.documentation}`)
              .join('\n')}`
          },
          insertText: funcName,
          range,
          detail: sig.label
        }
      })

      return { suggestions }
    }
  })

  // Hover docs
  monaco.languages.registerHoverProvider('javascript', {
    provideHover: (model, position) => {
      const word = model.getWordAtPosition(position)
      if (!word) return null

      const functionName = word.word
      const signature = p5Signatures[functionName]

      if (!signature) return null

      return {
        range: new monaco.Range(
          position.lineNumber,
          word.startColumn,
          position.lineNumber,
          word.endColumn
        ),
        contents: [
          { value: `**${signature.label}**` },
          { value: signature.documentation },
          { value: '**Parameters:**' },
          ...signature.parameters.map((p) => ({
            value: `- \`${p.label}\`: ${p.documentation}`
          }))
        ]
      }
    }
  })

  // TypeScript / JS defaults and p5 global typings
  monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
    target: monaco.languages.typescript.ScriptTarget.ES2020,
    allowNonTsExtensions: true,
    moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
    module: monaco.languages.typescript.ModuleKind.CommonJS,
    noEmit: true,
    esModuleInterop: true,
    lib: ['es2020', 'dom']
  })

  monaco.languages.typescript.javascriptDefaults.addExtraLib(
    `
      // p5.js Core Functions
      declare function createCanvas(w: number, h: number, renderer?: string): any;
      declare function resizeCanvas(w: number, h: number): void;
      declare function noCanvas(): void;
      declare function createGraphics(w: number, h: number, renderer?: string): any;
      declare function blendMode(mode: string): void;
      
      // Structure
      declare function setup(): void;
      declare function draw(): void;
      declare function preload(): void;
      declare function remove(): void;
      declare function noLoop(): void;
      declare function loop(): void;
      declare function push(): void;
      declare function pop(): void;
      declare function redraw(): void;
      
      // Shape Drawing
      declare function rect(x: number, y: number, w: number, h?: number, tl?: number, tr?: number, br?: number, bl?: number): void;
      declare function square(x: number, y: number, s: number, tl?: number, tr?: number, br?: number, bl?: number): void;
      declare function ellipse(x: number, y: number, w: number, h?: number): void;
      declare function circle(x: number, y: number, d: number): void;
      declare function line(x1: number, y1: number, x2: number, y2: number): void;
      declare function triangle(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number): void;
      declare function point(x: number, y: number): void;
      declare function quad(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, x4: number, y4: number): void;
      declare function arc(x: number, y: number, w: number, h: number, start: number, stop: number, mode?: string): void;
      declare function bezier(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, x4: number, y4: number): void;
      
      // Color Functions
      declare function background(v1: number | string, v2?: number, v3?: number, alpha?: number): void;
      declare function fill(v1: number | string, v2?: number, v3?: number, alpha?: number): void;
      declare function noFill(): void;
      declare function stroke(v1: number | string, v2?: number, v3?: number, alpha?: number): void;
      declare function noStroke(): void;
      declare function strokeWeight(weight: number): void;
      declare function color(v1: number | string, v2?: number, v3?: number, alpha?: number): any;
      declare function colorMode(mode: string, max?: number): void;
      
      // Transform
      declare function translate(x: number, y: number): void;
      declare function rotate(angle: number): void;
      declare function scale(s: number | number[], y?: number): void;
      
      // Math & Random
      declare function random(min?: number, max?: number): number;
      declare function randomSeed(seed: number): void;
      declare function noise(x: number, y?: number, z?: number): number;
      declare function noiseSeed(seed: number): void;
      declare function map(value: number, start1: number, stop1: number, start2: number, stop2: number): number;
      declare function constrain(n: number, low: number, high: number): number;
      declare function dist(x1: number, y1: number, x2: number, y2: number): number;
      declare function lerp(start: number, stop: number, amt: number): number;
      declare function mag(a: number, b: number): number;
      declare function norm(value: number, start: number, stop: number): number;
      declare function sq(n: number): number;
      declare function sqrt(n: number): number;
      declare function abs(n: number): number;
      declare function floor(n: number): number;
      declare function ceil(n: number): number;
      declare function round(n: number): number;
      declare function sin(angle: number): number;
      declare function cos(angle: number): number;
      declare function tan(angle: number): number;
      
      // System Variables
      declare var width: number;
      declare var height: number;
      declare var mouseX: number;
      declare var mouseY: number;
      declare var pmouseX: number;
      declare var pmouseY: number;
      declare var mouseIsPressed: boolean;
      declare var key: string;
      declare var keyCode: number;
      declare var keyIsPressed: boolean;
      declare var frameCount: number;
      declare var deltaTime: number;
      
      // Events
      declare function mousePressed(): void;
      declare function mouseReleased(): void;
      declare function mouseMoved(): void;
      declare function mouseDragged(): void;
      declare function mouseClicked(): void;
      declare function keyPressed(): void;
      declare function keyReleased(): void;
      declare function keyTyped(): void;
    `,
    'p5.d.ts'
  )
}
